import type { FetchOptions } from "../helpers/backendRequestHelpers";
import {
  fetchRequest,
  reactQueryDelete,
  reactQueryPost,
  reactQueryPut,
} from "../helpers/backendRequestHelpers";
import { internalApi } from "../helpers/urlHelpers";
import { QAQuestionsApi } from "./QAQuestionsApi";
import { QARepliesApi } from "./QARepliesApi";
import { spotlightApi } from "./spotlightApi";

interface CreateParams extends FetchOptions {
  formData: Record<string, unknown>;
}

const create = <T>({ formData, ...fetchRequestOptions }: CreateParams) =>
  fetchRequest<T>(internalApi.liveStreams.create(), {
    method: "POST",
    body: JSON.stringify(formData),
    ...fetchRequestOptions,
  });

interface UpdateParams {
  id?: string | number;
  formData: Record<string, unknown>;
}

const update = <T>({ id, formData }: UpdateParams) =>
  reactQueryPut<T>(internalApi.liveStreams.update({ id }), formData);

interface GoLiveParams extends FetchOptions {
  id?: string | number;
}

const goLive = <T>({ id, ...fetchRequestOptions }: GoLiveParams) =>
  fetchRequest<T>(internalApi.liveStreams.goLive({ id }), {
    method: "PUT",
    ...fetchRequestOptions,
  });

interface DownloadParticipantsCSVParams extends FetchOptions {
  id?: string | number;
}

const downloadParticipantsCSV = <T>({
  id,
  ...fetchRequestOptions
}: DownloadParticipantsCSVParams) =>
  fetchRequest<T>(internalApi.liveStreams.downloadParticipantsCSV({ id }), {
    method: "GET",
    ...fetchRequestOptions,
  });

interface ChatTranscriptParams extends FetchOptions {
  id?: string | number;
}

const chatTranscript = <T>({
  id,
  ...fetchRequestOptions
}: ChatTranscriptParams) =>
  fetchRequest<T>(internalApi.liveStreams.chatTranscript({ id }), {
    method: "GET",
    ...fetchRequestOptions,
  });

interface JoinParams extends FetchOptions {
  id?: string | number;
  role: string;
}

const join = <T>({ id, role, ...fetchRequestOptions }: JoinParams) =>
  fetchRequest<T>(internalApi.liveStreams.join({ id }), {
    method: "POST",
    body: JSON.stringify({ role }),
    ...fetchRequestOptions,
  });

interface LeaveParams extends FetchOptions {
  id?: string | number;
  participantId?: string | number;
}

const leave = <T>({ id, participantId, ...fetchRequestOptions }: LeaveParams) =>
  fetchRequest<T>(internalApi.liveStreams.leave({ id, participantId }), {
    method: "DELETE",
    ...fetchRequestOptions,
  });

interface AddCohostsParams extends FetchOptions {
  roomId?: string | number;
  formData: Record<string, unknown>;
}

const addCohosts = <T>({
  roomId,
  formData,
  ...fetchRequestOptions
}: AddCohostsParams) =>
  fetchRequest<T>(internalApi.liveStreams.addCohosts({ roomId }), {
    method: "POST",
    body: JSON.stringify(formData),
    ...fetchRequestOptions,
  });

interface AddModeratorParams extends FetchOptions {
  roomId?: string | number;
  formData: Record<string, unknown>;
}

const addModerator = <T>({
  roomId,
  formData,
  ...fetchRequestOptions
}: AddModeratorParams) =>
  fetchRequest<T>(internalApi.liveStreams.addModerator({ roomId }), {
    method: "POST",
    body: JSON.stringify(formData),
    ...fetchRequestOptions,
  });

interface InviteParticipantsParams {
  roomId?: string | number;
  formData: {
    community_member_ids: string[];
  };
}

const inviteParticipants = <T>({
  roomId,
  formData,
}: InviteParticipantsParams) =>
  reactQueryPost<T>(
    internalApi.liveStreams.inviteParticipants({ roomId }),
    formData,
  );

interface RemoveCohostParams extends FetchOptions {
  id?: string | number;
  roomId?: string | number;
}

const removeCohost = <T>({
  id,
  roomId,
  ...fetchRequestOptions
}: RemoveCohostParams) =>
  fetchRequest<T>(internalApi.liveStreams.removeCohost({ id, roomId }), {
    method: "DELETE",
    ...fetchRequestOptions,
  });

interface ChangeRoleParams extends FetchOptions {
  roomId?: string | number;
  participantId?: string | number;
  role: string;
}

const changeRole = <T>({
  roomId,
  participantId,
  role,
  ...fetchRequestOptions
}: ChangeRoleParams) =>
  fetchRequest<T>(
    internalApi.liveStreams.changeRole({ roomId, participantId }),
    {
      method: "PATCH",
      body: JSON.stringify({ role }),
      ...fetchRequestOptions,
    },
  );

interface RemoveParticipantParams {
  roomId?: string | number;
  participantId?: string | number;
  peerId: string | number;
}

const removeParticipant = <T>({
  roomId,
  participantId,
  peerId,
}: RemoveParticipantParams) =>
  reactQueryPost<T>(
    internalApi.liveStreams.removeParticipant({
      roomId,
      participantId,
    }),
    {
      peerId,
    },
  );

interface EndStreamParams extends FetchOptions {
  id?: string | number;
}

const endStream = <T>({ id, ...fetchRequestOptions }: EndStreamParams) =>
  fetchRequest<T>(internalApi.liveStreams.endStream({ id }), {
    method: "DELETE",
    ...fetchRequestOptions,
  });

interface EndStreamDelayedParams extends FetchOptions {
  id?: string | number;
}

const endStreamDelayed = <T>({
  id,
  ...fetchRequestOptions
}: EndStreamDelayedParams) =>
  fetchRequest<T>(internalApi.liveStreams.endStreamDelayed({ id }), {
    method: "DELETE",
    ...fetchRequestOptions,
  });

interface LeaveFeedbackParams extends FetchOptions {
  id?: string | number;
  participantId?: string | number;
  formData: {
    score: number;
    message: string;
    device_info: string;
  };
}

const leaveFeedback = <T>({
  id,
  participantId,
  formData,
  ...fetchRequestOptions
}: LeaveFeedbackParams) =>
  fetchRequest<T>(
    internalApi.liveStreams.leaveFeedback({ id, participantId }),
    {
      method: "POST",
      body: JSON.stringify(formData),
      ...fetchRequestOptions,
    },
  );

interface CreateRecorderParams {
  id?: string | number;
  recording_key: string | number;
}

const createRecorder = <T>({ id, recording_key }: CreateRecorderParams) =>
  reactQueryPost<T>(internalApi.liveStreams.createRecorder({ id }), {
    recording_key,
  });

interface DeleteRoomRecordingParams {
  roomId?: string | number;
  id?: string | number;
}

const deleteRoomRecording = <T>({ roomId, id }: DeleteRoomRecordingParams) =>
  reactQueryDelete<T>(
    internalApi.liveStreams.deleteRoomRecording({ roomId, id }),
  );

interface UpdateQASessionParams {
  roomId?: string | number;
  formData: Record<string, unknown>;
}

const updateQASession = <T>({ roomId, formData }: UpdateQASessionParams) =>
  reactQueryPut<T>(
    internalApi.liveStreams.updateQASession({ roomId }),
    formData,
  );

export const liveStreamApi = {
  create,
  update,
  goLive,
  downloadParticipantsCSV,
  chatTranscript,
  join,
  leave,
  addCohosts,
  removeCohost,
  inviteParticipants,
  changeRole,
  endStream,
  endStreamDelayed,
  leaveFeedback,
  createRecorder,
  deleteRoomRecording,
  removeParticipant,
  addModerator,
  updateQASession,
  ...QAQuestionsApi,
  ...QARepliesApi,
  ...spotlightApi,
};
